import React from 'react';
import { Grid } from 'lucide-react';
import { SimulationResults } from '../types/api';

interface ConfusionMatrixProps {
  results: SimulationResults;
}

const ConfusionMatrix: React.FC<ConfusionMatrixProps> = ({ results }) => {
  if (!results.ground_truth_distribution || !results.confusion_matrix) {
    return null;
  }

  // Use pre-calculated confusion matrix from backend (calculated on all responses)
  const { tp: truePositives, fp: falsePositives, tn: trueNegatives, fn: falseNegatives } = results.confusion_matrix;

  const total = truePositives + falsePositives + trueNegatives + falseNegatives;
  
  if (total === 0) {
    return null;
  }

  const tpPercent = (truePositives / total) * 100;
  const fpPercent = (falsePositives / total) * 100;
  const tnPercent = (trueNegatives / total) * 100;
  const fnPercent = (falseNegatives / total) * 100;

  const precision = truePositives / (truePositives + falsePositives) || 0;
  const recall = truePositives / (truePositives + falseNegatives) || 0;
  const specificity = trueNegatives / (trueNegatives + falsePositives) || 0;
  const f1Score = 2 * (precision * recall) / (precision + recall) || 0;

  const getIntensityClass = (value: number, maxValue: number) => {
    const intensity = value / maxValue;
    if (intensity === 0) return 'bg-muted text-foreground';
    if (intensity < 0.3) return 'bg-muted text-foreground';
    if (intensity < 0.6) return 'bg-foreground/30 text-foreground';
    return 'bg-foreground text-background';
  };

  const maxValue = Math.max(truePositives, falsePositives, trueNegatives, falseNegatives);

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-center space-x-2 mb-5">
        <Grid className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">Confusion Matrix</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Matrix */}
        <div>
          <h3 className="text-xs font-medium text-muted-foreground mb-3">Matrix</h3>
          
          <div className="border rounded-md overflow-hidden">
            <div className="grid grid-cols-3 bg-muted">
              <div className="p-2 text-center text-xs font-medium"></div>
              <div className="p-2 text-center text-xs font-medium border-l">Pred: Yes</div>
              <div className="p-2 text-center text-xs font-medium border-l">Pred: No</div>
            </div>
            
            <div className="grid grid-cols-3 border-t">
              <div className="p-2 bg-muted text-xs font-medium border-r">Real: Yes</div>
              <div className={`p-2 border-r text-center ${getIntensityClass(truePositives, maxValue)}`}>
                <div className="font-semibold">{truePositives}</div>
                <div className="text-[10px] opacity-70">TP ({tpPercent.toFixed(0)}%)</div>
              </div>
              <div className={`p-2 text-center ${getIntensityClass(falseNegatives, maxValue)}`}>
                <div className="font-semibold">{falseNegatives}</div>
                <div className="text-[10px] opacity-70">FN ({fnPercent.toFixed(0)}%)</div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 border-t">
              <div className="p-2 bg-muted text-xs font-medium border-r">Real: No</div>
              <div className={`p-2 border-r text-center ${getIntensityClass(falsePositives, maxValue)}`}>
                <div className="font-semibold">{falsePositives}</div>
                <div className="text-[10px] opacity-70">FP ({fpPercent.toFixed(0)}%)</div>
              </div>
              <div className={`p-2 text-center ${getIntensityClass(trueNegatives, maxValue)}`}>
                <div className="font-semibold">{trueNegatives}</div>
                <div className="text-[10px] opacity-70">TN ({tnPercent.toFixed(0)}%)</div>
              </div>
            </div>
          </div>

          <div className="mt-3 text-[10px] text-muted-foreground space-y-0.5">
            <p>TP: True Positive | FP: False Positive</p>
            <p>TN: True Negative | FN: False Negative</p>
          </div>
        </div>

        {/* Metrics */}
        <div>
          <h3 className="text-xs font-medium text-muted-foreground mb-3">Metrics</h3>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="border rounded-md p-2.5">
              <div className="text-[10px] text-muted-foreground mb-0.5">Precision</div>
              <div className="text-lg font-semibold">{(precision * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-muted-foreground">
                TP / (TP + FP)
              </div>
            </div>

            <div className="border rounded-md p-2.5">
              <div className="text-[10px] text-muted-foreground mb-0.5">Recall</div>
              <div className="text-lg font-semibold">{(recall * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-muted-foreground">
                TP / (TP + FN)
              </div>
            </div>

            <div className="border rounded-md p-2.5">
              <div className="text-[10px] text-muted-foreground mb-0.5">Specificity</div>
              <div className="text-lg font-semibold">{(specificity * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-muted-foreground">
                TN / (TN + FP)
              </div>
            </div>

            <div className="border rounded-md p-2.5">
              <div className="text-[10px] text-muted-foreground mb-0.5">F1-Score</div>
              <div className="text-lg font-semibold">{(f1Score * 100).toFixed(1)}%</div>
              <div className="text-[10px] text-muted-foreground">
                Harmonic mean
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interpretation */}
      <div className="mt-4 p-3 bg-muted/30 rounded-md">
        <h4 className="text-xs font-medium mb-2">Interpretation</h4>
        <div className="grid grid-cols-2 gap-3 text-[10px] text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">Strengths:</span>
            <ul className="mt-0.5 space-y-0.5">
              {precision > 0.8 && <li>High precision</li>}
              {recall > 0.8 && <li>Good recall</li>}
              {specificity > 0.8 && <li>Good specificity</li>}
              {f1Score > 0.8 && <li>Balanced F1</li>}
              {precision <= 0.8 && recall <= 0.8 && specificity <= 0.8 && f1Score <= 0.8 && <li>Room for improvement</li>}
            </ul>
          </div>
          <div>
            <span className="font-medium text-foreground">Issues:</span>
            <ul className="mt-0.5 space-y-0.5">
              {precision < 0.7 && <li>High false positives</li>}
              {recall < 0.7 && <li>High false negatives</li>}
              {specificity < 0.7 && <li>Low specificity</li>}
              {f1Score < 0.7 && <li>Imbalanced metrics</li>}
              {precision >= 0.7 && recall >= 0.7 && specificity >= 0.7 && f1Score >= 0.7 && <li>None significant</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfusionMatrix;
